"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  SearchableSelections,
  ComboBoxListContent,
} from "./searchable-combo-box-parts";
import type { SearchableComboBoxProps } from "./searchable-combo-box-parts";
export type { SearchableComboBoxProps } from "./searchable-combo-box-parts";
export { SearchableComboBoxCreateButton } from "./searchable-combo-box-parts";
export function SearchableComboBox<T>({
  type = "multi",
  identifier,
  items,
  selectedItems,
  onItemRemoved,
  nameGetter,
  displayNameGetter,
  itemKey,
  onInputTextChange,
  autoFocus,
  enterAdvances,
  placeholder,
  label,
  disabled,
  inputContainerClassName,
  triggerClassName,
  renderSearchBoxLeading = () => undefined,
  renderCommandEmpty,
  renderCommandItem,
  renderItem,
  onDismiss,
  onInputKeyDown,
}: SearchableComboBoxProps<T>) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const suppressFocusOpenRef = useRef(false);
  const focusTriggerAfterSelectRef = useRef(false);
  const autoFocusedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const generatedId = useId();
  const listId = `${identifier || generatedId}-listbox`;

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const selectionKey: unknown = itemKey
    ? selectedItems.map(itemKey).join("|")
    : selectedItems;
  const selectionKeyRef = useRef(selectionKey);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const filteredItems = useMemo(() => {
    const query = inputText.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const name = (nameGetter?.(item) ?? "").toLowerCase();
      return name.includes(query);
    });
  }, [items, inputText, nameGetter]);

  const emptyContent =
    filteredItems.length === 0 ? renderCommandEmpty({ inputText }) : null;
  const showDropdown =
    popoverOpen &&
    (inputText.length > 0 || items.length > 0) &&
    (filteredItems.length > 0 || emptyContent != null);

  useEffect(() => {
    if (disabled) {
      setPopoverOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    const selectionChanged = selectionKeyRef.current !== selectionKey;
    selectionKeyRef.current = selectionKey;
    if (!selectionChanged) return;

    if (type === "multi") {
      setInputText("");
      if (popoverOpen) {
        inputRef.current?.focus();
      }
      return;
    }

    // Clearing the value mid-edit must not wipe what the user is typing.
    if (selectedItems.length === 0) return;
    if (popoverOpen && rootRef.current?.contains(document.activeElement)) {
      focusTriggerAfterSelectRef.current = true;
    }
    setPopoverOpen(false);
    setInputText("");
  }, [selectionKey, selectedItems.length, type, popoverOpen]);

  useEffect(() => {
    if (!autoFocus || disabled || autoFocusedRef.current) return;
    autoFocusedRef.current = true;
    suppressFocusOpenRef.current = true;
    (triggerRef.current ?? inputRef.current)?.focus();
    suppressFocusOpenRef.current = false;
  }, [autoFocus, disabled]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [inputText]);

  useEffect(() => {
    if (!popoverOpen) return;
    const onOutsidePress = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        rootRef.current?.contains(target) ||
        (rootRef.current && event.composedPath().includes(rootRef.current))
      )
        return;
      onDismissRef.current?.({
        inputText,
        matchCount: filteredItems.length,
      });
      setPopoverOpen(false);
      if (type === "single") {
        setInputText("");
      }
    };
    // An in-flow menu must not collapse and move the clicked button before click fires.
    const eventType = rootRef.current?.closest(
      '[data-flow-presentation="embedded"], [data-flow-chrome="lead"]',
    )
      ? "click"
      : "pointerdown";
    document.addEventListener(eventType, onOutsidePress);
    return () => document.removeEventListener(eventType, onOutsidePress);
  }, [filteredItems.length, inputText, popoverOpen, type]);

  const showClosedSingleTrigger =
    type === "single" && selectedItems.length > 0 && !popoverOpen;

  useEffect(() => {
    if (!showClosedSingleTrigger || !focusTriggerAfterSelectRef.current) return;
    focusTriggerAfterSelectRef.current = false;
    triggerRef.current?.focus();
  }, [showClosedSingleTrigger]);

  const showInput =
    type === "multi" ||
    popoverOpen ||
    (type === "single" && selectedItems.length === 0);

  const openFromTrigger = () => {
    setPopoverOpen(true);
    const item = selectedItems[0];
    if (item) {
      setInputText((displayNameGetter ?? nameGetter)?.(item) ?? "");
    }
    queueMicrotask(() => inputRef.current?.focus());
  };

  return (
    <>
      <Command
        label={label ?? placeholder ?? "Search options"}
        className="h-auto w-full overflow-visible bg-transparent"
        shouldFilter={false}
        onKeyDown={(event) => {
          if (popoverOpen) return;
          if (
            event.key === "ArrowDown" ||
            event.key === "ArrowUp" ||
            event.key === "Home" ||
            event.key === "End" ||
            event.key === "Enter"
          ) {
            event.preventDefault();
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              openFromTrigger();
            }
          }
        }}
      >
        <div ref={rootRef} className="relative w-full">
          {showClosedSingleTrigger ? (
            <Button
              ref={triggerRef}
              type="button"
              variant="outline"
              data-questionnaire-enter-advance={
                enterAdvances ? "true" : undefined
              }
              className={cn(
                "w-full justify-start font-normal cursor-text hover:bg-transparent h-[38px] px-3 py-2",
                triggerClassName,
              )}
              aria-expanded={popoverOpen}
              aria-controls={listId}
              disabled={disabled}
              onClick={openFromTrigger}
            >
              {renderItem?.({ item: selectedItems[0] })}
            </Button>
          ) : null}

          {showInput ? (
            <CommandInput
              ref={inputRef}
              asChild
              placeholder={placeholder}
              aria-controls={listId}
              aria-expanded={showDropdown}
              value={inputText}
              onValueChange={(value) => {
                setInputText(value);
                onInputTextChange?.(value);
                if (!disabled) {
                  setPopoverOpen(true);
                }
              }}
              data-questionnaire-enter-advance={
                enterAdvances && filteredItems.length === 0 ? "true" : undefined
              }
              className="h-full min-h-9 border-0 focus:ring-0 focus:border-0"
              containerClassName={
                inputContainerClassName ?? "border rounded-md"
              }
              data-cmdk-input-id={identifier}
              onFocus={() => {
                if (!disabled && !suppressFocusOpenRef.current) {
                  setPopoverOpen(true);
                }
              }}
              onClick={() => {
                if (!disabled) setPopoverOpen(true);
              }}
              onKeyDown={(event) =>
                onInputKeyDown?.(event, {
                  inputText,
                  matchCount: filteredItems.length,
                })
              }
              disabled={disabled}
              leading={renderSearchBoxLeading({ inputText })}
            >
              <input
                role="combobox"
                aria-expanded={showDropdown}
                aria-controls={listId}
              />
            </CommandInput>
          ) : null}

          <div
            hidden={!showDropdown}
            className="absolute left-0 right-0 top-full z-[1400] mt-1.5 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md group-data-[flow-presentation=embedded]/flow:static group-data-[flow-presentation=embedded]/flow:shadow-sm group-data-[flow-chrome=lead]/flow:static group-data-[flow-chrome=lead]/flow:shadow-sm"
          >
            <CommandList ref={listRef} asChild>
              <ComboBoxListContent id={listId}>
                {showDropdown ? (
                  <>
                    {emptyContent}

                    <CommandGroup>
                      {filteredItems.map((item) => renderCommandItem({ item }))}
                    </CommandGroup>
                  </>
                ) : null}
              </ComboBoxListContent>
            </CommandList>
          </div>
        </div>
      </Command>

      <SearchableSelections
        type={type}
        selectedItems={selectedItems}
        onItemRemoved={onItemRemoved}
        renderItem={renderItem}
      />
    </>
  );
}

SearchableComboBox.displayName = "SearchableComboBox";
